import styles from './styles.module.scss';
import { CounterProps } from './props';
import CounterSekeleton from './Skeleton';
import { useTranslation } from '../../hooks';

/**
 * Component that renders a countdown timer with days, hours, minutes and seconds
 * Can be customized to show/hide days and hours, use smaller size, and different background color
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether to show loading skeleton state
 * @param {Object} props.countdown - Object containing countdown values
 * @param {number} props.countdown.days - Number of days remaining
 * @param {number} props.countdown.hours - Number of hours remaining
 * @param {number} props.countdown.minutes - Number of minutes remaining
 * @param {number} props.countdown.seconds - Number of seconds remaining
 * @param {boolean} props.isSmall - Whether to use smaller size styling
 * @param {boolean} props.hideDays - Whether to hide the days counter
 * @param {boolean} props.hideHours - Whether to hide the hours counter
 * @param {boolean} props.isContainerColor - Whether to use container background color
 * @returns {JSX.Element} Countdown timer display with configurable units
 */
export function Counter({
  isLoading,
  countdown,
  isSmall,
  hideDays,
  hideHours,
  isContainerColor,
}: CounterProps) {
  const { t } = useTranslation('tournaments');
  const { days, hours, minutes, seconds } = countdown;

  // Show loading skeleton if isLoading is true
  if (isLoading) {
    return <CounterSekeleton />;
  }

  return (
    <div
      className={`${styles.row} ${isSmall ? styles['row--small'] : ''} ${hideDays ? styles['row--hide-days'] : ''} ${hideDays && hideHours ? styles['row--hide-hours'] : ''} ${isContainerColor ? styles['row--container-color'] : ''}`}
    >
      {!hideDays && (
        <div className={styles.row__wrapper}>
          <div
            className={`${styles.row__wrapper__card} ${isSmall ? styles['row__wrapper__card--small'] : ''}`}
          >
            {days}
          </div>
          <div
            className={`${styles.row__wrapper__label} ${isSmall ? styles['row__wrapper__label--small'] : ''}`}
          >
            {t('tournaments:days')}
          </div>
        </div>
      )}
      {!hideHours && (
        <div className={styles.row__wrapper}>
          <div
            className={`${styles.row__wrapper__card} ${isSmall ? styles['row__wrapper__card--small'] : ''}`}
          >
            {hours}
          </div>
          <div
            className={`${styles.row__wrapper__label} ${isSmall ? styles['row__wrapper__label--small'] : ''}`}
          >
            {t('tournaments:hours')}
          </div>
        </div>
      )}
      <div className={styles.row__wrapper}>
        <div
          className={`${styles.row__wrapper__card} ${isSmall ? styles['row__wrapper__card--small'] : ''}`}
        >
          {minutes}
        </div>
        <div
          className={`${styles.row__wrapper__label} ${isSmall ? styles['row__wrapper__label--small'] : ''}`}
        >
          {t('tournaments:minutes')}
        </div>
      </div>
      <div className={styles.row__wrapper}>
        <div
          className={`${styles.row__wrapper__card} ${isSmall ? styles['row__wrapper__card--small'] : ''}`}
        >
          {seconds}
        </div>
        <div
          className={`${styles.row__wrapper__label} ${isSmall ? styles['row__wrapper__label--small'] : ''}`}
        >
          {t('tournaments:seconds')}
        </div>
      </div>
    </div>
  );
}
